import { Box, Flex, Text } from "@chakra-ui/react";
import { MapContainer, TileLayer } from "react-leaflet";
import { MapPinned, Locate, VectorSquare } from "lucide-react";
import { useState } from "react";

export type LocationType = "point" | "area";

interface LocationTypeSelectorProps {
  onSelect: (type: LocationType) => void;
  t: (key: string) => string;
}

export function LocationTypeSelector({
  onSelect,
  t,
}: LocationTypeSelectorProps) {
  const [hoveredButton, setHoveredButton] = useState<LocationType | null>(null);

  const buttonBaseStyle = {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    padding: "16px",
    backgroundColor: "#FFF",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    flex: "1 1 0",
    maxWidth: "220px",
    minHeight: "130px",
    transition: "all 0.2s ease-in-out",
  };

  const getButtonStyle = (type: LocationType) => ({
    ...buttonBaseStyle,
    transform: hoveredButton === type ? "scale(1.02)" : "scale(1)",
    boxShadow:
      hoveredButton === type ? "0 4px 12px rgba(0, 0, 0, 0.15)" : "none",
  });

  return (
    <Box borderWidth="1px" borderRadius="md" p={4}>
      <Flex gap="12px" mb="16px">
        <MapPinned color="#086AA9" size="20px" />
        <Text fontWeight={"400"} fontSize="md" color="#086AA9">
          {t("monitoredLocation")}
        </Text>
      </Flex>

      <div
        style={{
          position: "relative",
          height: "256px",
          width: "100%",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <MapContainer
          center={[0, 0]}
          zoom={2}
          style={{
            height: "100%",
            width: "100%",
            filter: "grayscale(80%) brightness(0.7)",
            borderRadius: "8px",
          }}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          zoomControl={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
        </MapContainer>

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            color: "white",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <button
            type="button"
            style={getButtonStyle("point")}
            onMouseEnter={() => setHoveredButton("point")}
            onMouseLeave={() => setHoveredButton(null)}
            onClick={() => onSelect("point")}
          >
            <Flex direction="column" align="center" gap="4px">
              <Locate size={20} color="#0884CC" />
              <Text
                fontSize="16px"
                fontWeight="500"
                color="#0884CC"
                lineHeight="1.4"
              >
                {t("point")}
              </Text>
            </Flex>
            <Text
              fontSize="13px"
              fontWeight="400"
              color="#4A6075"
              textAlign="center"
              lineHeight="1.4"
            >
              {t("pointDescription")}
            </Text>
          </button>

          <button
            type="button"
            style={getButtonStyle("area")}
            onMouseEnter={() => setHoveredButton("area")}
            onMouseLeave={() => setHoveredButton(null)}
            onClick={() => onSelect("area")}
          >
            <Flex direction="column" align="center" gap="4px">
              <VectorSquare size={20} color="#0884CC" />
              <Text
                fontSize="16px"
                fontWeight="500"
                color="#0884CC"
                lineHeight="1.4"
              >
                {t("area")}
              </Text>
            </Flex>
            <Text
              fontSize="13px"
              fontWeight="400"
              color="#4A6075"
              textAlign="center"
              lineHeight="1.4"
            >
              {t("areaDescription")}
            </Text>
          </button>
        </div>
      </div>
    </Box>
  );
}
